import { api, CHAT_TAG } from '@/api';
import { setChatData } from '@/store';
import { handleQueryLifecycle } from '@/api/utils.ts';
import { toPlainTextCitations } from '@/utils/htmlToPlainText';
import type { ChatMessageStatus, DraftInfo } from '@/types/chat';

export interface GetChatRequest {
  chatId: string | undefined;
}

export enum Sender {
  USER = 'USER',
  AI = 'AI',
}

export interface Citation {
  documentId: string | null;
  /** Set instead of `documentId` for a history row with no document; the panel shows its details. */
  historyRowId?: string | null;
  pageNumber: number | null;
  extract: string;
  /** The extract is text a model produced from an image; see `isAiGeneratedCitation`. */
  citesAiGeneratedContent?: boolean;
}

export interface NextStepsMessage {
  nextStepsMessageId: string;
  title: string;
  prompt: string;
}

export interface ChatMessage {
  messageId: string;
  sender: Sender;
  message: string;
  citations: Record<string, Citation>;
  nextStepsMessages?: NextStepsMessage[];
  drafts?: DraftInfo[];
  status: ChatMessageStatus;
}

export interface GetChatResponse {
  chatTitle: string;
  citations: Record<string, Citation>;
  chatMessages: Array<ChatMessage>;
  chatId: string;
  canEdit: boolean;
  /** True only for the user who started the chat; shared participants get `false`. */
  isCreator: boolean;
  /** ISO datetime of the chat's most recent response; null before the first one. */
  lastResponse: string | null;
  pipelineId: string | null;
  matter: {
    matterId: string;
    matterReference: string;
  } | null;
}

const getChatApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getChat: builder.query<GetChatResponse, GetChatRequest>({
      query: ({ chatId }) => ({
        url: '/chat',
        method: 'GET',
        params: { chat_id: chatId },
      }),
      providesTags: (_result, _error, arg) => [{ type: CHAT_TAG, id: arg.chatId }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await handleQueryLifecycle(queryFulfilled, (data) => {
          dispatch(setChatData(data));
        });
      },
      transformResponse: (response: GetChatResponse, _, arg) => {
        return {
          ...response,
          chatId: arg.chatId || '',
          // Extracts come out of the ingestion pipeline as HTML; every consumer renders
          // them as plain text.
          citations: toPlainTextCitations(response.citations),
        };
      },
    }),
  }),
});

export const { useGetChatQuery, useLazyGetChatQuery } = getChatApi;
